import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type ReactNode
} from 'react'
import {
  ArrowUp,
  Check,
  ChevronDown,
  ChevronRight,
  Clock3,
  FileText,
  MessageSquarePlus,
  Paperclip,
  Pencil,
  RotateCw,
  Square,
  Trash2,
  X
} from 'lucide-react'

import {
  clearChatHandoff,
  fetchAgentConversationAttachment,
  getThinkingLevelPreference,
  selectChatHandoff,
  useAppDispatch,
  useAppSelector,
  useBatchUpdateCustomAgentResultsMutation,
  isDemoMode,
  effortLevelLabel,
  MODEL_EFFORT_LEVEL_OPTIONS,
  setThinkingLevelPreference,
  useGetAuthzMeQuery,
  useGetModelPolicyQuery,
  type ConversationAttachment,
  type ConversationRun,
  type ChatCustomAgentBulkBatch,
  type ChatCustomAgentRun,
  type LiveSegment,
  type ModelOption,
  type OrchestratorEffortSelection,
  type OrchestratorModelSelection,
  type QueuedOrchestratorMessage
} from '@axilon-apps/domains-ontology-ui-state'
import {
  CHAT_ATTACHMENT_ACCEPT,
  type ChatAttachmentErrorAction
} from '@atbash-labs/ontology-agent-wire'

import { formatRelativeTime } from '../../lib/time'
import { cn } from '../../lib/utils'
import { SourceDocViewer, useSourceDocViewer } from '../SourceDocViewer'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '../ui/alert-dialog'
import { Button } from '../ui/button'
import { Card, CardContent } from '../ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from '../ui/dropdown-menu'
import { ScrollArea } from '../ui/scroll-area'
import { Textarea } from '../ui/textarea'
import { ChatLandingCards, ChatLandingHero } from './ChatLanding'
import { OpenWorkList, useOpenWorkState } from './OpenWorkList'
import type { LandingTaskCard } from './chat-landing'
import { CHAT_LANDING_MEASURE, CHAT_PAGE_COLUMN, CHAT_PAGE_MEASURE } from './chat-measure'
import { ConversationList } from './ConversationList'
import { useSuggestedChatPrompts } from './suggested-prompts'
import { AgentActionApprovalPanel } from './AgentActionApprovalPanel'
import { isAgentModeSwitchConsumed, isAgentModeSwitchRerun } from './agentModeSwitch'
import { resolveChatEmptyPrompts } from './chat-behavior'
import { ChatTurn, pastTurnToolCallEntries } from './ChatTurn'
import { handoffChipText, withHandoffChip } from './handoff-chip'
import { collectChatArtifactRefs, previewOwnerByArtifactId } from './chatArtifacts'
import {
  canApplyScrollTop,
  peekTranscriptScroll,
  readTranscriptScroll,
  rememberTranscriptScroll,
  shouldRestoreTranscriptScroll
} from './transcript-scroll'
import {
  AUTO_MODEL_LABEL,
  composerTriggerModelLabel,
  clampEffortToCatalog,
  partitionModelOptions,
  resolveModelSelection,
  selectionEffortLevels
} from './model-selector-labels'
import { useOrchestratorChatController } from './useOrchestratorChatController'

const BackgroundAgentsCard = lazy(() =>
  import('./CustomAgentRunCard').then((module) => ({
    default: module.BackgroundAgentsCard
  }))
)

const BulkAgentBatchesCard = lazy(() =>
  import('./BulkAgentBatchCards').then((module) => ({
    default: module.BulkAgentBatchesCard
  }))
)

type OrchestratorChatController = ReturnType<typeof useOrchestratorChatController>

export function OrchestratorPane({
  chat,
  emptyStatePrompts,
  composerPlaceholder,
  layout = 'rail',
  showLanding = true,
  composerHeader
}: {
  chat: OrchestratorChatController
  /** Rendered inside the composer above the textarea (e.g. pinned context chips). */
  composerHeader?: ReactNode
  /** Override welcome sample prompts; `[]` hides them. Default: plant-aware prompts. */
  emptyStatePrompts?: string[]
  /** Override empty-composer placeholder (e.g. ingest-scoped Ask lane). */
  composerPlaceholder?: string
  /** Page density: centered measure and the chat-first landing. */
  layout?: 'rail' | 'page'
  /**
   * Set false to keep the page measure but drop the greeting + starter cards —
   * a job-scoped lane is already about one thing and has nothing to welcome.
   */
  showLanding?: boolean
}) {
  return (
    <SidebarChat
      chat={chat}
      emptyStatePrompts={emptyStatePrompts}
      composerPlaceholder={composerPlaceholder}
      layout={layout}
      showLanding={showLanding}
      composerHeader={composerHeader}
    />
  )
}

export function OrchestratorHistory({
  chat,
  onClose,
  onFocusOrchestrator
}: {
  chat: OrchestratorChatController
  onClose: () => void
  onFocusOrchestrator: () => void
}) {
  return (
    <ConversationList
      conversations={chat.conversations}
      conversationsLoaded={chat.conversationsLoaded}
      activeTabId={chat.activeTabId}
      openTabIds={chat.openTabs}
      onNewTab={() => {
        chat.handleNewTab()
        onFocusOrchestrator()
        onClose()
      }}
      onSelect={(id) => {
        void chat.handleSelectFromHistory(id)
        onFocusOrchestrator()
        onClose()
      }}
      onRename={chat.handleRename}
      onDelete={chat.handleRequestDelete}
    />
  )
}

export function OrchestratorDeleteDialog({ chat }: { chat: OrchestratorChatController }) {
  return (
    <AlertDialog
      open={chat.pendingDeleteId !== null}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) chat.handleCancelDelete()
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete conversation?</AlertDialogTitle>
          <AlertDialogDescription>
            {chat.pendingDeleteConversation
              ? `"${chat.pendingDeleteConversation.title}" and all of its turns will be permanently removed.`
              : 'This conversation and all of its turns will be permanently removed.'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {chat.deleteError ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
            {chat.deleteError}
          </div>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={chat.deleteInFlight}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            variant="outline"
            className="border-st-down/30 bg-st-down-bg text-st-down hover:bg-st-down-bg hover:text-st-down dark:border-st-down/40 dark:bg-st-down-bg dark:hover:bg-st-down-bg"
            disabled={chat.deleteInFlight}
            onClick={(event) => {
              event.preventDefault()
              void chat.handleConfirmDelete()
            }}
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

// Larger than any realistic scrollHeight. Assigning it to `scrollTop` pins the
// viewport to the bottom (the browser clamps to the max) WITHOUT reading a
// layout property — reading `scrollHeight` on every streamed token forces a
// synchronous reflow of the whole transcript, which was the dominant chat jank.
const SCROLL_PIN_SENTINEL = 1_000_000_000

// How long a reopened transcript keeps trying to reach its saved offset.
// Turns, markdown, and tool output land over several frames, so the height
// needed for that offset does not exist at first paint.
const RESTORE_SETTLE_MS = 2_000
const LIVE_TURN_KEY = '__live_artifact_preview__'

const SidebarChat = ({
  chat,
  emptyStatePrompts: emptyStatePromptsOverride,
  composerPlaceholder,
  layout,
  showLanding,
  composerHeader
}: {
  chat: OrchestratorChatController
  emptyStatePrompts?: string[]
  composerPlaceholder?: string
  layout: 'rail' | 'page'
  showLanding: boolean
  composerHeader?: ReactNode
}) => {
  const bottomRef = useRef<HTMLDivElement>(null)
  const { data: authz } = useGetAuthzMeQuery()
  const emptyStatePrompts = emptyStatePromptsOverride ?? resolveChatEmptyPrompts(isDemoMode())
  const queuedMessages = chat.queuedMessages ?? []
  const customAgentRuns: Record<string, ChatCustomAgentRun> = chat.customAgentRuns ?? {}
  const customAgentBulkBatches: Record<string, ChatCustomAgentBulkBatch> =
    chat.customAgentBulkBatches ?? {}
  const [queuedExpanded, setQueuedExpanded] = useState(true)
  const previewArtifactIdsByTurn = useMemo(() => {
    const byTurn = new Map<string, Set<string>>()
    if (layout !== 'page') return byTurn
    const owners = previewOwnerByArtifactId([
      ...chat.pastTurns.map((turn) => ({
        key: turn.id,
        refs: collectChatArtifactRefs(pastTurnToolCallEntries(turn))
      })),
      ...(chat.showLiveRow
        ? [
            {
              key: LIVE_TURN_KEY,
              refs: collectChatArtifactRefs(chat.liveToolCalls)
            }
          ]
        : [])
    ])
    for (const [artifactId, turnKey] of owners) {
      const ids = byTurn.get(turnKey)
      if (ids) ids.add(artifactId)
      else byTurn.set(turnKey, new Set([artifactId]))
    }
    return byTurn
  }, [chat.liveToolCalls, chat.pastTurns, chat.showLiveRow, layout])
  const queuedScopeRef = useRef<{
    conversationId: string | null
    count: number
  }>({
    conversationId: chat.activeTabId,
    count: 0
  })

  useEffect(() => {
    const previous = queuedScopeRef.current
    const scopeChanged = previous.conversationId !== chat.activeTabId
    if (queuedMessages.length > 0 && (scopeChanged || previous.count === 0)) {
      setQueuedExpanded(true)
    }
    queuedScopeRef.current = {
      conversationId: chat.activeTabId,
      count: queuedMessages.length
    }
  }, [chat.activeTabId, queuedMessages.length])

  // Total characters streamed into the live turn so far. Any growth means
  // new tokens arrived — used to keep the viewport pinned to the bottom
  // while content streams in. The old effect only fired on turn-level
  // changes, so mid-stream the view could strand on blank space when
  // step sections auto-collapsed above it.
  const streamedCharCount = useMemo(() => {
    let total = chat.liveFinalSummary?.length ?? 0
    for (const text of Object.values(chat.pendingProgressByStep) as string[]) {
      total += text.length
    }
    for (const text of Object.values(chat.interStepTextByStep) as string[]) {
      total += text.length
    }
    for (const segments of Object.values(chat.liveSegmentsByStep) as LiveSegment[][]) {
      for (const segment of segments) total += segment.text.length
    }
    return total
  }, [
    chat.liveFinalSummary,
    chat.pendingProgressByStep,
    chat.interStepTextByStep,
    chat.liveSegmentsByStep
  ])

  // A new prompt still pins to the latest turn. Remounting the pane or
  // switching conversations restores a scrolled-up offset instead.
  const lastInstructionRef = useRef<string | undefined>(undefined)
  // The first pin of a mount jumps instead of animating.
  const firstPinRef = useRef(true)
  // Sticky-bottom: remember whether the user was following the stream
  // *before* the next layout. Reading distance-from-bottom only after a
  // CoT collapse / live→past swap is too late — content has already
  // shrunk and the viewport appears "scrolled up" to the user question.
  const stickToBottomRef = useRef(true)
  const prevShowLiveRowRef = useRef(chat.showLiveRow)
  // Coalesces the streaming auto-scroll to at most one write per animation
  // frame, even when several concurrent tab streams each trigger this effect
  // within the same frame.
  const pinScrollRafRef = useRef<number | null>(null)
  // Offset this conversation should reopen at, until it has been applied.
  const restoreTargetRef = useRef<number | null>(null)
  const restoreFrameRef = useRef<number | null>(null)
  // Last offset written programmatically. Scroll events are delivered a frame
  // later, so the echo of our own write has to be told apart from the user
  // reaching for the scrollbar.
  const appliedTopRef = useRef<number | null>(null)

  const findViewport = useCallback(
    (): HTMLElement | null =>
      bottomRef.current?.closest<HTMLElement>('[data-slot=scroll-area-viewport]') ?? null,
    []
  )

  const inFlightAgentActionCount = Object.keys(chat.inFlightAgentActions).length

  // ChatTurn is memoized; handleSwitchToAgentMode is a stable identity
  // that resubmits this turn's prompt as a full orchestrator run.
  // Locked composer modes pass no callback: the marker is stripped
  // but no button renders.
  const switchToAgentMode =
    chat.askModeLocked || !chat.canContribute ? undefined : chat.handleSwitchToAgentMode

  useEffect(
    () => () => {
      if (pinScrollRafRef.current !== null && typeof cancelAnimationFrame === 'function') {
        cancelAnimationFrame(pinScrollRafRef.current)
      }
    },
    []
  )

  useLayoutEffect(() => {
    if (pinScrollRafRef.current !== null && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(pinScrollRafRef.current)
      pinScrollRafRef.current = null
    }
    appliedTopRef.current = null
    const saved = peekTranscriptScroll(layout, chat.activeTabId)
    if (shouldRestoreTranscriptScroll(saved)) {
      stickToBottomRef.current = false
      restoreTargetRef.current = saved.scrollTop
      firstPinRef.current = false
      return
    }
    stickToBottomRef.current = true
    restoreTargetRef.current = null
  }, [layout, chat.activeTabId])

  // Keep reaching for the saved offset until the transcript is tall enough to
  // hold it. Turns, markdown, and tool output land over several frames, and
  // assigning too early just clamps the viewport back to the top.
  useLayoutEffect(() => {
    if (restoreTargetRef.current === null) return undefined
    let cancelled = false
    const deadline = Date.now() + RESTORE_SETTLE_MS

    const tick = (): void => {
      restoreFrameRef.current = null
      if (cancelled) return
      const target = restoreTargetRef.current
      const viewport = findViewport()
      if (target === null || viewport === null) return
      if (canApplyScrollTop(viewport, target)) {
        appliedTopRef.current = target
        viewport.scrollTop = target
        restoreTargetRef.current = null
        return
      }
      if (Date.now() >= deadline || typeof requestAnimationFrame !== 'function') {
        restoreTargetRef.current = null
        return
      }
      restoreFrameRef.current = requestAnimationFrame(tick)
    }

    tick()
    return () => {
      cancelled = true
      if (restoreFrameRef.current !== null && typeof cancelAnimationFrame === 'function') {
        cancelAnimationFrame(restoreFrameRef.current)
      }
      restoreFrameRef.current = null
    }
  }, [layout, chat.activeTabId, findViewport])

  useEffect(() => {
    const viewport = findViewport()
    if (viewport === null) return undefined
    const tabId = chat.activeTabId

    const onScroll = (): void => {
      // The echo of a scroll we just performed is not the user changing their mind.
      if (appliedTopRef.current !== null && viewport.scrollTop === appliedTopRef.current) return
      appliedTopRef.current = null
      restoreTargetRef.current = null
      const memory = readTranscriptScroll(viewport)
      if (memory === null) return
      stickToBottomRef.current = memory.stickToBottom
      rememberTranscriptScroll(layout, tabId, memory)
    }
    viewport.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      viewport.removeEventListener('scroll', onScroll)
      // A restore that never got its height keeps the offset it was aiming for.
      const target = restoreTargetRef.current
      if (target !== null) {
        rememberTranscriptScroll(layout, tabId, {
          scrollTop: target,
          stickToBottom: false
        })
        return
      }
      const memory = readTranscriptScroll(viewport)
      if (memory !== null) rememberTranscriptScroll(layout, tabId, memory)
    }
  }, [layout, chat.activeTabId, findViewport])

  useEffect(() => {
    const node = bottomRef.current
    if (node === null) return undefined

    const instructionKey = chat.activeInstruction ?? ''
    const instructionChanged =
      lastInstructionRef.current !== undefined && lastInstructionRef.current !== instructionKey
    lastInstructionRef.current = instructionKey
    const streamEnded = prevShowLiveRowRef.current === true && chat.showLiveRow === false
    prevShowLiveRowRef.current = chat.showLiveRow
    const force = instructionChanged || streamEnded

    // Don't yank a restored or user-scrolled offset, including on remount.
    if (restoreTargetRef.current !== null) return
    if (!force && !stickToBottomRef.current) return
    stickToBottomRef.current = true
    appliedTopRef.current = null

    const viewport = node.closest<HTMLElement>('[data-slot=scroll-area-viewport]')

    // Force transitions (new prompt / stream end) are rare, so an exact smooth
    // scroll — which must read scrollHeight — is affordable there.
    if (force && viewport !== null && typeof viewport.scrollTo === 'function') {
      const behavior = firstPinRef.current ? 'auto' : 'smooth'
      firstPinRef.current = false
      viewport.scrollTo({ top: viewport.scrollHeight, behavior })
      return
    }
    firstPinRef.current = false

    // High-frequency streaming path. This effect was the #1 chat CPU hotspot
    // under long transcripts / many tabs / slow CPUs, dominated by forced
    // synchronous layout. Two rules keep it cheap:
    //   1) Coalesce to ONE scroll per animation frame even if the effect runs
    //      several times per frame (each concurrent tab stream dispatches).
    //   2) Pin to the bottom with a WRITE-ONLY scrollTop sentinel rather than
    //      reading scrollHeight — the browser clamps during its normal layout
    //      pass instead of forcing an extra reflow of the whole transcript.
    if (pinScrollRafRef.current !== null) return
    if (typeof requestAnimationFrame === 'function') {
      pinScrollRafRef.current = requestAnimationFrame(() => {
        pinScrollRafRef.current = null
        const v = bottomRef.current?.closest<HTMLElement>('[data-slot=scroll-area-viewport]')
        if (v != null) v.scrollTop = SCROLL_PIN_SENTINEL
        else bottomRef.current?.scrollIntoView?.({ block: 'end' })
      })
    } else if (viewport !== null) {
      // Environments without rAF (jsdom): scroll immediately.
      viewport.scrollTop = SCROLL_PIN_SENTINEL
    } else if (typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ block: 'end' })
    }
  }, [
    chat.pastTurns.length,
    chat.showLiveRow,
    chat.activeInstruction,
    chat.plan,
    chat.activeTabId,
    streamedCharCount,
    inFlightAgentActionCount
  ])

  // The welcome examples surface only when the user is on a fresh,
  // turn-less surface AND no error is showing. Two states qualify:
  //   - pending new-chat (activeTabId === null) — no real conversation yet.
  //   - an open tab whose conversation has zero persisted turns and no
  //     in-flight run.
  const showWelcome = chat.showEmptyState && chat.canContribute
  // Layout exception: the greeting + 2×2 card chrome is page-only. The
  // rail offers a single starter chip from the same prompt list.
  const showPageLanding =
    showLanding && layout === 'page' && showWelcome && chat.activeTabId === null
  const skipLineInventory = isDemoMode() || !showPageLanding
  const suggestedCards = useSuggestedChatPrompts({ skip: skipLineInventory })
  // The chat home leads with the user's open work; canned prompts are the
  // fallback when nothing is open (and the demo keeps its scripted prompts).
  const openWork = useOpenWorkState({
    skip: isDemoMode() || emptyStatePromptsOverride !== undefined || !showWelcome
  })

  const pickLandingCard = (card: LandingTaskCard): void => {
    chat.setAskMode(card.mode === 'ask')
    chat.setDraft(card.prompt)
    document.querySelector<HTMLTextAreaElement>('[data-tour-id="chat-composer"]')?.focus()
  }

  return (
    <div className={cn('flex min-h-0 flex-1 flex-col', showPageLanding && 'justify-center')}>
      {showPageLanding ? (
        <div className="mb-6 duration-300 animate-in fade-in-0 motion-reduce:animate-none">
          <ChatLandingHero />
        </div>
      ) : null}
      {chat.activeTabId !== null || chat.liveTurnAttachmentPreviews.length > 0 ? (
        <ConversationAttachmentsPanel
          conversationId={chat.activeTabId}
          attachments={chat.activeConversationAttachments}
          livePreviews={chat.liveTurnAttachmentPreviews}
          loaded={chat.activeConversationAttachmentsLoaded}
          error={chat.attachmentLoadError}
          layout={layout}
        />
      ) : null}
      {showPageLanding ? null : (
        <ScrollArea
          className={cn(
            'min-h-0 flex-1 [&>[data-slot=scroll-area-viewport]>div]:!block',
            layout === 'page' && CHAT_PAGE_COLUMN
          )}
        >
          <div
            className={cn(
              'flex flex-col gap-4 px-6 py-4',
              layout === 'page' && cn(CHAT_PAGE_MEASURE, 'pt-4 text-15')
            )}
          >
            {chat.streamError ? (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                {chat.streamError}
              </div>
            ) : null}
            {chat.showEmptyState && chat.activeTabId !== null && chat.canContribute ? (
              <Card className="bg-muted/40 py-4">
                <CardContent className="px-4 text-sm text-muted-foreground">
                  This conversation has no turns yet. Submit a prompt to start.
                </CardContent>
              </Card>
            ) : null}

            {chat.pastTurns.map((turn: ConversationRun, index: number) => (
              <ChatTurn
                key={turn.id}
                kind="past"
                turn={turn}
                conversationId={chat.activeTabId ?? undefined}
                attachments={chat.activeConversationAttachments}
                layout={layout}
                previewArtifactIds={previewArtifactIdsByTurn.get(turn.id)}
                onSwitchToAgentMode={switchToAgentMode}
                hidePromptAsModeSwitch={isAgentModeSwitchRerun(
                  turn.prompt,
                  chat.pastTurns[index - 1]
                )}
                hideAgentModeSwitch={isAgentModeSwitchConsumed(
                  turn,
                  chat.pastTurns[index + 1]?.prompt ??
                    (index === chat.pastTurns.length - 1
                      ? (chat.agentModeSwitchPrompt ?? undefined)
                      : undefined)
                )}
                onForkFromHere={() => void chat.handleForkFromHere(turn.id)}
                forkInFlight={chat.copyInFlight}
              />
            ))}

            {chat.showLiveRow && chat.activeInstruction ? (
              <ChatTurn
                kind="live"
                conversationId={chat.activeTabId ?? undefined}
                attachments={chat.activeConversationAttachments}
                layout={layout}
                previewArtifactIds={previewArtifactIdsByTurn.get(LIVE_TURN_KEY)}
                prompt={chat.activeInstruction}
                plan={chat.plan}
                stepResults={chat.stepResults}
                toolCalls={chat.liveToolCalls}
                liveSegmentsByStep={chat.liveSegmentsByStep}
                pendingProgressByStep={chat.pendingProgressByStep}
                interStepTextByStep={chat.interStepTextByStep}
                modelFallback={chat.modelFallback}
                contextCompacted={chat.contextCompacted}
                inFlightIngestWaits={chat.inFlightIngestWaits}
                hasInFlightCypherWrites={Object.keys(chat.inFlightAgentActions).length > 0}
                finalSummary={chat.liveFinalSummary}
                onSwitchToAgentMode={switchToAgentMode}
                hidePromptAsModeSwitch={
                  (chat.agentModeSwitchPrompt !== null &&
                    chat.agentModeSwitchPrompt === chat.activeInstruction.trim()) ||
                  isAgentModeSwitchRerun(
                    chat.activeInstruction,
                    chat.pastTurns[chat.pastTurns.length - 1]
                  )
                }
                hideAgentModeSwitch={
                  chat.agentModeSwitchPrompt !== null &&
                  chat.agentModeSwitchPrompt === chat.activeInstruction.trim()
                }
              />
            ) : null}

            <div ref={bottomRef} />
          </div>
        </ScrollArea>
      )}

      {chat.canContribute && Object.keys(chat.inFlightAgentActions).length > 0 ? (
        <AgentActionApprovalPanel
          proposals={chat.inFlightAgentActions}
          conversationId={chat.activeTabId}
          permissions={authz?.permissions}
          className={
            layout === 'page'
              ? cn(CHAT_PAGE_MEASURE, 'border-t-0 bg-transparent pb-1 backdrop-blur-none')
              : undefined
          }
        />
      ) : null}

      {chat.activeTabId && Object.keys(customAgentRuns).length > 0 ? (
        <div
          className={cn(
            'py-2',
            layout !== 'page' && 'px-6',
            layout === 'page' && CHAT_PAGE_MEASURE
          )}
        >
          <Suspense fallback={null}>
            <BackgroundAgentsCard
              conversationId={chat.activeTabId}
              runs={Object.values(customAgentRuns)}
              canCancel={chat.canContribute}
            />
          </Suspense>
        </div>
      ) : null}

      {chat.activeTabId && Object.keys(customAgentBulkBatches).length > 0 ? (
        <div
          className={cn(
            'py-2',
            layout !== 'page' && 'px-6',
            layout === 'page' && CHAT_PAGE_MEASURE
          )}
        >
          <Suspense fallback={null}>
            <BulkAgentBatchesCard
              conversationId={chat.activeTabId}
              batches={Object.values(customAgentBulkBatches)}
              canCancel={chat.canContribute}
            />
          </Suspense>
        </div>
      ) : null}

      {queuedMessages.length > 0 ? (
        <div className={cn(layout === 'page' && CHAT_PAGE_MEASURE)}>
          <div className="border-t bg-muted/20">
            <button
              type="button"
              className={cn(
                'flex w-full cursor-pointer items-center gap-1.5 py-2.5 text-left text-10 font-medium tracking-[0.06em] text-muted-foreground uppercase transition-colors hover:text-foreground',
                layout !== 'page' && 'px-6'
              )}
              aria-expanded={queuedExpanded}
              aria-controls="queued-orchestrator-messages"
              aria-label={queuedExpanded ? 'Collapse queued messages' : 'Expand queued messages'}
              onClick={() => setQueuedExpanded((expanded) => !expanded)}
            >
              {queuedExpanded ? (
                <ChevronDown className="size-3" />
              ) : (
                <ChevronRight className="size-3" />
              )}
              <Clock3 className="size-3" />
              <span>Queued</span>
              <span className="rounded-full bg-muted px-1.5 py-0.5 text-9 tabular-nums">
                {queuedMessages.length}
              </span>
            </button>
            {queuedExpanded ? (
              <div
                id="queued-orchestrator-messages"
                className={cn('flex flex-col gap-1.5 pb-3', layout !== 'page' && 'px-6')}
              >
                {queuedMessages.map((message: QueuedOrchestratorMessage) => (
                  <QueuedMessageCard
                    key={message.id}
                    message={message}
                    onEdit={chat.handleEditQueuedMessage}
                    onDelete={chat.handleDeleteQueuedMessage}
                    canEdit={chat.canContribute}
                  />
                ))}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {showWelcome &&
      chat.activeTabId === null &&
      !showPageLanding &&
      (openWork.items.length > 0 || openWork.isError || openWork.isLoading) ? (
        <OpenWorkList
          items={openWork.items}
          error={openWork.isError}
          loading={openWork.isLoading}
          retry={openWork.retry}
          layout="rail"
        />
      ) : showWelcome && chat.activeTabId === null && !showPageLanding ? (
        <div
          className={cn('flex flex-col gap-1 px-6 pb-3', layout === 'page' && CHAT_PAGE_MEASURE)}
        >
          {emptyStatePrompts.map((prompt) => (
            <button
              key={prompt}
              data-tour-id={welcomePromptTourId(prompt)}
              type="button"
              onClick={() => chat.setDraft(prompt)}
              className="flex w-full cursor-pointer items-center gap-2 text-left text-11 text-muted-foreground transition-colors hover:text-foreground"
            >
              <ChevronRight className="size-3 shrink-0 opacity-60" />
              <span className="min-w-0 truncate">{prompt}</span>
            </button>
          ))}
        </div>
      ) : null}

      {chat.canContribute ? (
        <ChatComposer
          chat={chat}
          emptyPlaceholder={composerPlaceholder}
          layout={layout}
          landing={showPageLanding}
          composerHeader={composerHeader}
        />
      ) : chat.activeTabId === null ? (
        <div
          className={cn(
            'text-sm text-muted-foreground',
            layout === 'page' ? cn(CHAT_PAGE_MEASURE, 'pt-1 pb-6') : 'border-t px-6 py-4'
          )}
        >
          Loading conversation…
        </div>
      ) : (
        <ReadOnlyConversationCallout chat={chat} layout={layout} />
      )}
      {showPageLanding ? (
        <div className="pt-3 duration-300 animate-in fade-in-0 motion-reduce:animate-none">
          {openWork.items.length > 0 || openWork.isError || openWork.isLoading ? (
            <OpenWorkList
              items={openWork.items}
              error={openWork.isError}
              loading={openWork.isLoading}
              retry={openWork.retry}
              layout="page"
            />
          ) : (
            <ChatLandingCards cards={suggestedCards} onPickPrompt={pickLandingCard} />
          )}
        </div>
      ) : null}
    </div>
  )
}

function welcomePromptTourId(prompt: string): string | undefined {
  const lower = prompt.toLowerCase()
  if (lower.includes('dashboard')) return 'chat-prompt-dashboard'
  if (lower.includes('servicenow')) return 'chat-prompt-servicenow'
  if (lower.includes('rca')) return 'chat-prompt-rca'
  if (lower.includes('pm config')) return 'chat-prompt-pm'
  if (lower.includes('oee')) return 'chat-prompt-oee'
  return undefined
}

function QueuedMessageCard({
  message,
  onEdit,
  onDelete,
  canEdit
}: {
  message: QueuedOrchestratorMessage
  onEdit: (messageId: string, instruction: string) => Promise<void>
  onDelete: (messageId: string) => Promise<void>
  canEdit: boolean
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(message.instruction)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const save = async (): Promise<void> => {
    const instruction = draft.trim()
    if (!instruction || instruction === message.instruction) {
      setDraft(message.instruction)
      setEditing(false)
      return
    }
    setBusy(true)
    setError(null)
    try {
      await onEdit(message.id, instruction)
      setEditing(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to edit queued message.')
    } finally {
      setBusy(false)
    }
  }

  const remove = async (): Promise<void> => {
    setBusy(true)
    setError(null)
    try {
      await onDelete(message.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to delete queued message.')
      setBusy(false)
    }
  }

  return (
    <div className="rounded-md border border-dashed border-primary/40 bg-primary/5 px-3 py-2 text-xs text-foreground">
      {editing ? (
        <>
          <Textarea
            rows={2}
            value={draft}
            disabled={busy}
            aria-label="Edit queued message"
            onChange={(event) => setDraft(event.target.value)}
          />
          <div className="mt-1.5 flex justify-end gap-1">
            <Button
              type="button"
              variant="ghost"
              size="xs"
              disabled={busy}
              onClick={() => {
                setDraft(message.instruction)
                setEditing(false)
                setError(null)
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="outline"
              size="xs"
              disabled={busy || draft.trim().length === 0}
              onClick={() => void save()}
            >
              <Check className="size-3" />
              Save
            </Button>
          </div>
        </>
      ) : (
        <div className="flex items-start gap-2">
          <span className="min-w-0 flex-1 whitespace-pre-wrap">{message.instruction}</span>
          {canEdit ? (
            <div className="flex shrink-0 gap-0.5">
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                aria-label="Edit queued message"
                title="Edit queued message"
                disabled={busy}
                onClick={() => setEditing(true)}
              >
                <Pencil className="size-3" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                className="text-destructive hover:text-destructive"
                aria-label="Delete queued message"
                title="Delete queued message"
                disabled={busy}
                onClick={() => void remove()}
              >
                <Trash2 className="size-3" />
              </Button>
            </div>
          ) : null}
        </div>
      )}
      {error ? <div className="mt-1.5 text-11 text-destructive">{error}</div> : null}
    </div>
  )
}

function ReadOnlyConversationCallout({
  chat,
  layout
}: {
  chat: OrchestratorChatController
  layout: 'rail' | 'page'
}) {
  return (
    <div
      className={cn(layout === 'page' ? cn(CHAT_PAGE_MEASURE, 'pt-1 pb-6') : 'border-t px-6 py-4')}
    >
      <div className="rounded-lg border bg-muted/30 p-3">
        <p className="text-sm font-medium text-foreground">This shared chat is view-only</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Start a new chat to continue from this conversation’s history.
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-3"
          disabled={chat.copyInFlight}
          onClick={() => void chat.handleStartNewChatFromThis()}
        >
          <MessageSquarePlus className="size-4" />
          {chat.copyInFlight ? 'Starting new chat…' : 'Start new chat from this'}
        </Button>
        {chat.copyError ? <p className="mt-2 text-xs text-destructive">{chat.copyError}</p> : null}
      </div>
    </div>
  )
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function ChatComposer({
  chat,
  emptyPlaceholder,
  layout,
  landing,
  composerHeader
}: {
  chat: OrchestratorChatController
  emptyPlaceholder?: string
  layout: 'rail' | 'page'
  landing: boolean
  composerHeader?: ReactNode
}) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { data: authz, isLoading: authzLoading } = useGetAuthzMeQuery()
  const canUseChat = authz?.permissions.includes('chat/use') ?? false
  const dispatch = useAppDispatch()
  // A work page ("Ask in chat" / "Reject" on a task or problem) handed this
  // composer an item: show it as a chip, and on send mark rejects with the
  // typed reason before the message (prefixed with the chip marker) goes out.
  const handoff = useAppSelector(selectChatHandoff)
  const [batchUpdateResults] = useBatchUpdateCustomAgentResultsMutation()
  const rejectNeedsReason = handoff?.tone === 'reject' && chat.draft.trim().length === 0
  const backgroundRun = Object.values(
    (chat.customAgentRuns ?? {}) as Record<string, ChatCustomAgentRun>
  )[0]
  const backgroundBatch = Object.values(
    (chat.customAgentBulkBatches ?? {}) as Record<string, ChatCustomAgentBulkBatch>
  )[0]
  const backgroundRunsLoading = chat.activeTabId !== null && chat.customAgentRunsLoading === true
  const canCompose =
    canUseChat &&
    backgroundRun === undefined &&
    backgroundBatch === undefined &&
    !backgroundRunsLoading

  const handleBrowse = (): void => {
    fileInputRef.current?.click()
  }

  // File picker and clipboard paste share the controller's policy validation.
  const stageFiles = (files: File[]): boolean => {
    if (files.length === 0) return false
    chat.addAttachments(files)
    return true
  }

  const handleFiles = (event: ChangeEvent<HTMLInputElement>): void => {
    const picked = Array.from(event.target.files ?? [])
    event.target.value = ''
    stageFiles(picked)
  }

  // Pull image files out of a paste so users can paste screenshots /
  // copied images straight into the chat. Clipboard images usually arrive
  // with a generic name (or none), so we synthesize a unique, descriptive
  // filename per paste. Non-image clipboard content (plain text) falls
  // through to the textarea's default handling. Image staging is skipped
  // without preventDefault when attachments cannot be added.
  const handlePaste = (event: ClipboardEvent<HTMLTextAreaElement>): void => {
    const imageItems = Array.from(event.clipboardData.items).filter(
      (item) => item.kind === 'file' && item.type.startsWith('image/')
    )
    if (imageItems.length === 0) return
    if (!canUseChat || chat.hasSendingAttachments) return
    event.preventDefault()
    const pasted: File[] = []
    imageItems.forEach((item, index) => {
      const file = item.getAsFile()
      if (file === null) return
      const extension = file.type.split('/')[1] || 'png'
      const stamp = `${Date.now()}-${index}`
      const named = new File([file], `pasted-image-${stamp}.${extension}`, {
        type: file.type,
        lastModified: file.lastModified
      })
      pasted.push(named)
    })
    stageFiles(pasted)
  }

  // An active stream changes Send into Queue, but it does not lock the next
  // turn. Mode, model, and attachments are snapshotted when that turn is
  // submitted, so the queued request can safely use different settings.
  const canSubmit =
    canCompose &&
    !chat.hasSendingAttachments &&
    !chat.hasInvalidAttachments &&
    (chat.draft.trim().length > 0 || chat.stagedAttachments.length > 0)

  const submit = (): void => {
    if (!canCompose) return
    if (handoff === null) {
      void chat.handleSubmit(chat.draft)
      return
    }
    const draft = chat.draft
    const chip = { tone: handoff.tone, label: handoff.chipLabel }
    const rejectItemIds = handoff.tone === 'reject' ? handoff.rejectItemIds : []
    dispatch(clearChatHandoff())
    void (async () => {
      if (rejectItemIds.length > 0) {
        // Record the decision first so the agent sees the item as rejected.
        await batchUpdateResults({
          item_ids: rejectItemIds,
          approval_status: 'rejected',
          feedback: draft.trim().slice(0, 4096)
        })
      }
      await chat.handleSubmit(
        withHandoffChip(draft, chip),
        handoff.context
          ? { appContext: [{ key: 'Selected work', value: JSON.stringify(handoff.context) }] }
          : undefined
      )
    })()
  }

  const pageLayout = layout === 'page'
  const placeholder = handoff
    ? handoff.placeholder
    : pageLayout
      ? chat.activeTabId
        ? 'Ask a follow-up…'
        : (emptyPlaceholder ?? 'Ask about your plant…')
      : chat.activeTabId
        ? 'Continue this conversation...'
        : (emptyPlaceholder ?? 'Ask anything about the ontology...')
  const disabledPlaceholder = backgroundRun
    ? `Waiting for ${backgroundRun.name}…`
    : backgroundRunsLoading
      ? 'Checking background analyses…'
      : authzLoading
        ? 'Checking chat access…'
        : 'You do not have permission to use chat.'

  return (
    <div
      className={cn(
        pageLayout && cn(landing ? CHAT_LANDING_MEASURE : CHAT_PAGE_MEASURE, 'pt-1 pb-6'),
        !pageLayout && 'border-t px-6 py-4'
      )}
    >
      {chat.showContinueStoppedRun ? (
        <div className="mb-2 flex">
          <Button
            type="button"
            variant="outline"
            size="xs"
            className="gap-1.5"
            disabled={!canUseChat}
            onClick={() => {
              if (canUseChat) chat.handleContinueStoppedRun()
            }}
          >
            <RotateCw className="size-3" />
            Continue working
          </Button>
        </div>
      ) : null}
      <div
        className={cn(
          pageLayout && 'rounded-xl border border-border bg-card px-3 pt-2 pb-2 shadow-md'
        )}
      >
        {chat.stagedAttachments.length > 0 ? (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {chat.stagedAttachments.map((attachment) => (
              <AttachmentChip
                key={attachment.clientId}
                filename={attachment.filename}
                mimeType={attachment.mimeType}
                sizeBytes={attachment.sizeBytes}
                status={attachment.status}
                error={attachment.error}
                errorAction={attachment.errorAction}
                file={attachment.file}
                removeDisabled={attachment.status === 'sending'}
                onRemove={() => chat.removeAttachment(attachment.clientId)}
              />
            ))}
          </div>
        ) : null}
        {backgroundRun ? (
          <div
            className="mb-2 flex items-center justify-between gap-2 text-xs text-muted-foreground"
            role="status"
          >
            <span>Waiting for {backgroundRun.name} to return.</span>
            <Button type="button" variant="outline" size="xs" onClick={chat.handleNewTab}>
              New chat
            </Button>
          </div>
        ) : backgroundRunsLoading ? (
          <p className="mb-2 text-11 text-muted-foreground" role="status">
            Checking background analyses…
          </p>
        ) : !canUseChat ? (
          <p className="mb-2 text-11 text-muted-foreground" role="status">
            {authzLoading ? 'Checking chat access…' : 'You do not have permission to use chat.'}
          </p>
        ) : null}
        {handoff ? (
          <div className="mb-2 flex flex-wrap items-center gap-1.5">
            <span
              className={cn(
                'inline-flex max-w-full items-center gap-1 rounded-full border px-2.5 py-0.5 text-11 font-medium leading-snug',
                handoff.tone === 'reject'
                  ? 'border-st-down/35 bg-st-down-bg text-st-down'
                  : 'border-line bg-bg-sunk text-ink-2'
              )}
            >
              <span className="truncate">
                {handoffChipText({ tone: handoff.tone, label: handoff.chipLabel })}
              </span>
              <button
                type="button"
                aria-label="Remove attached item"
                className="-mr-1 shrink-0 cursor-pointer opacity-60 hover:opacity-100"
                onClick={() => dispatch(clearChatHandoff())}
              >
                <X className="size-3" />
              </button>
            </span>
          </div>
        ) : null}
        {composerHeader}
        <Textarea
          data-tour-id="chat-composer"
          rows={pageLayout ? 2 : 3}
          value={chat.draft}
          placeholder={canCompose ? placeholder : disabledPlaceholder}
          disabled={!canCompose}
          className={cn(
            // Grow with the draft, then scroll — never a drag-resize handle.
            'max-h-[40dvh] resize-none overflow-y-auto overscroll-contain',
            pageLayout &&
              'min-h-10 border-0 bg-transparent px-1 py-1.5 text-15 shadow-none focus-visible:ring-0 dark:bg-transparent'
          )}
          onChange={(event) => chat.setDraft(event.target.value)}
          onPaste={handlePaste}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault()
              if (canSubmit) submit()
            }
          }}
        />
        {rejectNeedsReason ? (
          <p className="m-0 mt-1 text-xs text-st-down">A reason is required to reject.</p>
        ) : null}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={CHAT_ATTACHMENT_ACCEPT}
          className="hidden"
          onChange={handleFiles}
        />
        <div className="mt-1 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size={pageLayout ? 'icon-sm' : 'xs'}
              aria-label="Attach files"
              title="Attach files (PDF, DOC, CSV, JSON, TXT, image, XLSX, .bak, .gwbk)"
              onClick={handleBrowse}
              disabled={!canCompose || chat.hasSendingAttachments}
            >
              <Paperclip className="size-3.5" />
            </Button>
            {chat.askModeLocked ? null : (
              <ComposerChatMode
                askMode={chat.askMode}
                onAskModeChange={chat.setAskMode}
                disabled={!canCompose}
              />
            )}
            <ComposerModelSettings
              modelSelection={chat.modelSelection}
              onModelChange={chat.setModelSelection}
              effortSelection={chat.effortSelection}
              onEffortChange={chat.setEffortSelection}
              disabled={!canCompose}
            />
          </div>
          {chat.showLiveRow ? (
            <div className="flex items-center gap-1.5">
              <Button
                type="button"
                variant="outline"
                size="xs"
                disabled={!canSubmit}
                onClick={submit}
              >
                Queue
              </Button>
              <Button
                type="button"
                variant="outline"
                size="xs"
                className="text-st-down hover:text-st-down"
                disabled={chat.cancelRequested}
                onClick={() => chat.handleStop()}
              >
                <Square className="fill-current" />
                {chat.cancelRequested ? 'Cancelling…' : 'Stop'}
              </Button>
            </div>
          ) : pageLayout ? (
            <Button
              type="button"
              data-tour-id="chat-submit"
              size="icon-sm"
              disabled={!canSubmit}
              aria-label="Send"
              onClick={submit}
            >
              <ArrowUp className="size-3.5" />
            </Button>
          ) : (
            <Button
              type="button"
              data-tour-id="chat-submit"
              variant="outline"
              size="xs"
              disabled={!canSubmit}
              onClick={submit}
            >
              {chat.activeTabId ? 'Continue' : 'Submit'}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Ask = query agent only (no planner). Agent = full orchestrator.
 * Hidden on surfaces that lock the run mode (Assist chat, ingest Ask).
 */
function ComposerChatMode({
  askMode,
  onAskModeChange,
  disabled
}: {
  askMode: boolean
  onAskModeChange: (enabled: boolean) => void
  disabled: boolean
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="xs"
          disabled={disabled}
          aria-label="Chat mode"
          title="Ask queries your factory. Agent creates artifacts."
        >
          {askMode ? 'Ask' : 'Agent'}
          <ChevronDown className="size-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuRadioGroup
          value={askMode ? 'ask' : 'agent'}
          onValueChange={(next) => onAskModeChange(next === 'ask')}
        >
          <DropdownMenuRadioItem value="ask" highlightSelected className="py-1 text-xs">
            <span className="flex items-center gap-1.5">
              <span>Ask</span>
              <span className="font-normal text-ink-3">Query your factory</span>
            </span>
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="agent" highlightSelected className="py-1 text-xs">
            <span className="flex items-center gap-1.5">
              <span>Agent</span>
              <span className="font-normal text-ink-3">Create artifacts</span>
            </span>
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** Radio value for "let the org policy pick" (no conversation override). */
const AUTO_MODEL_KEY = 'auto'

/**
 * Single Cursor-style composer control for per-conversation model + effort.
 * Org defaults live in Settings; this menu only overrides the current chat.
 * Hidden while the policy hasn't loaded or the org has no selectable models.
 */
function ComposerModelSettings({
  modelSelection,
  onModelChange,
  effortSelection,
  onEffortChange,
  disabled
}: {
  modelSelection: OrchestratorModelSelection | null
  onModelChange: (selection: OrchestratorModelSelection | null) => void
  effortSelection: OrchestratorEffortSelection | null
  onEffortChange: (selection: OrchestratorEffortSelection | null) => void
  disabled: boolean
}) {
  const { data: policy } = useGetModelPolicyQuery()
  const effortLevels = policy ? selectionEffortLevels(policy, modelSelection) : []
  const effortSupported = effortLevels.length > 0
  const selectedModel = policy ? resolveModelSelection(policy, modelSelection) : null
  const effectiveEffort =
    effortSupported && selectedModel !== null
      ? clampEffortToCatalog(
          effortLevels,
          effortSelection ?? getThinkingLevelPreference(selectedModel)
        )
      : null

  useEffect(() => {
    if (policy && modelSelection !== null && selectedModel === null) {
      onModelChange(null)
      onEffortChange(null)
    }
  }, [modelSelection, onEffortChange, onModelChange, policy, selectedModel])

  useEffect(() => {
    if (!effortSupported && effortSelection !== null) {
      onEffortChange(null)
      return
    }
    if (effortSupported && effortSelection === null && selectedModel !== null) {
      onEffortChange(getThinkingLevelPreference(selectedModel))
    }
  }, [effortSupported, effortSelection, onEffortChange, selectedModel])

  if (!policy || !Array.isArray(policy.catalog) || policy.catalog.length === 0) {
    return null
  }

  const { primary, other } = partitionModelOptions(policy)
  const modelKey =
    modelSelection === null ? AUTO_MODEL_KEY : `${modelSelection.provider}/${modelSelection.model}`
  // Surfaced on the submenu row so a selection made in there is still legible
  // from the first screen.
  const selectedOther = other.find((option) => `${option.provider}/${option.model}` === modelKey)

  const selectModel = (next: OrchestratorModelSelection | null): void => {
    onModelChange(next)
    const nextModel = resolveModelSelection(policy, next)
    const nextLevels = selectionEffortLevels(policy, next)
    if (nextModel !== null && nextLevels.length > 0) {
      onEffortChange(clampEffortToCatalog(nextLevels, getThinkingLevelPreference(nextModel)))
    } else if (effortSelection !== null) {
      onEffortChange(null)
    }
  }
  const selectThinkingLevel = (level: OrchestratorEffortSelection): void => {
    if (selectedModel !== null) setThinkingLevelPreference(selectedModel, level)
    onEffortChange(level)
  }
  const selectModelKey = (next: string): void => {
    if (next === AUTO_MODEL_KEY) {
      selectModel(null)
      return
    }
    const option = policy.catalog.find((entry) => `${entry.provider}/${entry.model}` === next)
    if (option) selectModel({ provider: option.provider, model: option.model })
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="xs"
          disabled={disabled}
          aria-label={
            effortSupported
              ? 'Model and effort for this conversation'
              : 'Model for this conversation'
          }
          title={
            effortSupported
              ? 'Model and thinking effort for this conversation'
              : 'Model for this conversation'
          }
        >
          {composerTriggerModelLabel(policy, modelSelection)}
          <ChevronDown className="size-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuRadioGroup value={modelKey} onValueChange={selectModelKey}>
          <DropdownMenuRadioItem
            value={AUTO_MODEL_KEY}
            highlightSelected
            className="py-1 text-xs"
            onSelect={(event) => event.preventDefault()}
          >
            {AUTO_MODEL_LABEL}
          </DropdownMenuRadioItem>
          {primary.map((option) => (
            <ComposerModelItem key={`${option.provider}/${option.model}`} option={option} />
          ))}
        </DropdownMenuRadioGroup>

        {/* The long tail lives one level down so the first screen stays short. */}
        {other.length > 0 ? (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger
              className="py-1 text-xs"
              aria-label={
                selectedOther ? `Other models, currently ${selectedOther.label}` : 'Other models'
              }
            >
              <span className="flex items-center gap-1.5">
                <span>Other models</span>
                {selectedOther ? (
                  <span className="max-w-24 truncate text-muted-foreground">
                    {selectedOther.label}
                  </span>
                ) : null}
              </span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="max-h-80 overflow-y-auto">
              <DropdownMenuRadioGroup value={modelKey} onValueChange={selectModelKey}>
                {other.map((option) => (
                  <ComposerModelItem key={`${option.provider}/${option.model}`} option={option} />
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        ) : null}

        {/* Auto has no Thinking row. Pinned models without an effort
            parameter also omit it. */}
        {effortSupported ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger
                className="py-1 text-xs"
                aria-label={`Edit thinking level, currently ${effectiveEffort}`}
              >
                <span className="flex items-center gap-1.5">
                  <span>Thinking</span>
                  <span className="text-muted-foreground">
                    {effortLevelLabel(effectiveEffort ?? '')}
                  </span>
                </span>
                <Pencil className="size-3" />
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuLabel className="px-2 py-1 text-10 font-medium tracking-wide text-ink-3 uppercase">
                  Thinking level
                </DropdownMenuLabel>
                <DropdownMenuRadioGroup
                  value={effectiveEffort ?? 'medium'}
                  onValueChange={(next) => selectThinkingLevel(next as OrchestratorEffortSelection)}
                >
                  {MODEL_EFFORT_LEVEL_OPTIONS.filter((option) =>
                    effortLevels.includes(option.value)
                  ).map((option) => (
                    <DropdownMenuRadioItem
                      key={option.value}
                      value={option.value}
                      highlightSelected
                      className="py-1 text-xs"
                      onSelect={(event) => event.preventDefault()}
                    >
                      {option.label}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** One catalog model row, shared by the main list and the "Other models" submenu. */
function ComposerModelItem({ option }: { option: ModelOption }) {
  return (
    <DropdownMenuRadioItem
      value={`${option.provider}/${option.model}`}
      highlightSelected
      className="py-1 text-xs"
      onSelect={(event) => event.preventDefault()}
    >
      <span className="truncate">{option.label}</span>
    </DropdownMenuRadioItem>
  )
}

/**
 * A single staged-attachment chip. For image files it renders an actual
 * thumbnail (so a pasted screenshot is recognizable at a glance) instead of
 * the generic paperclip icon. The thumbnail uses an object URL that is
 * created lazily and revoked on unmount / file change to avoid leaking blob
 * URLs as the user adds and removes attachments.
 */
function AttachmentChip({
  filename,
  mimeType,
  sizeBytes,
  status,
  error,
  errorAction,
  file,
  removeDisabled,
  onRemove
}: {
  filename: string
  mimeType: string
  sizeBytes: number
  status: 'staged' | 'sending' | 'failed'
  error: string | null
  errorAction?: ChatAttachmentErrorAction
  file: File | null
  removeDisabled: boolean
  onRemove: () => void
}) {
  const isImage = mimeType.startsWith('image/')
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!isImage || file === null) {
      setPreviewUrl(null)
      return
    }
    const url = URL.createObjectURL(file)
    setPreviewUrl(url)
    return () => {
      URL.revokeObjectURL(url)
    }
  }, [file, isImage])

  const statusLabel =
    status === 'sending' ? 'Sending' : status === 'failed' ? (error ?? 'Could not send') : null
  const title = statusLabel
    ? `${filename} (${formatBytes(sizeBytes)}) — ${statusLabel}`
    : `${filename} (${formatBytes(sizeBytes)})`

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md border py-0.5 pl-1 pr-2 text-11',
        status === 'failed'
          ? 'border-destructive/40 bg-destructive/10 text-destructive'
          : 'bg-muted/50 text-muted-foreground',
        status === 'sending' && 'opacity-70'
      )}
      title={title}
    >
      {previewUrl !== null ? (
        <img
          src={previewUrl}
          alt={filename}
          className="size-7 shrink-0 rounded-sm border border-border object-cover"
        />
      ) : (
        <Paperclip className="ml-1 size-3 opacity-60" />
      )}
      <span className="flex min-w-0 max-w-60 flex-col">
        <span className="truncate">{filename}</span>
        {status === 'failed' ? (
          <span className="line-clamp-2 text-10 leading-tight">
            {error ?? "Couldn't send the attached files. Try again."}
          </span>
        ) : null}
      </span>
      {status === 'failed' && errorAction === 'use_ingest' ? (
        <a
          href="/ingest"
          className="shrink-0 rounded px-1 py-0.5 font-medium underline underline-offset-2 hover:bg-destructive/10"
        >
          Use Ingest
        </a>
      ) : null}
      <button
        type="button"
        aria-label={`Remove ${filename}`}
        disabled={removeDisabled}
        className="ml-0.5 -mr-0.5 rounded p-0.5 hover:bg-muted disabled:pointer-events-none disabled:opacity-40"
        onClick={onRemove}
      >
        <X className="size-3" />
      </button>
    </span>
  )
}

interface ConversationAttachmentsPanelProps {
  conversationId: string | null
  attachments: ConversationAttachment[]
  livePreviews: ConversationAttachment[]
  loaded: boolean
  error: string | null
  layout: 'rail' | 'page'
}

/**
 * Slim collapsible row that surfaces every file the orchestrator has been
 * handed across the lifetime of the active conversation.
 */
function ConversationAttachmentsPanel({
  conversationId,
  attachments,
  livePreviews,
  loaded,
  error,
  layout
}: ConversationAttachmentsPanelProps) {
  const hasLivePreviews = livePreviews.length > 0
  const [open, setOpen] = useState(hasLivePreviews)
  const { openSource, viewerProps } = useSourceDocViewer()
  useEffect(() => {
    if (hasLivePreviews) setOpen(true)
  }, [hasLivePreviews])

  const totalCount = attachments.length + livePreviews.length
  const grouped = useMemo(() => groupAttachmentsByRun(attachments), [attachments])
  const openAttachment = useCallback(
    (attachment: ConversationAttachment): void => {
      if (conversationId === null) return
      openSource({
        sourceFile: attachment.filename,
        loadSource: () => fetchAgentConversationAttachment(conversationId, attachment.id),
        supportsOriginalDownload: false
      })
    },
    [conversationId, openSource]
  )

  if (!loaded && error === null && !hasLivePreviews) {
    return null
  }

  if (loaded && totalCount === 0 && error === null) {
    return null
  }

  return (
    <>
      <div
        className={cn(
          'border-b bg-muted/20 py-1.5',
          layout === 'page' ? CHAT_PAGE_MEASURE : 'px-6'
        )}
      >
        {error !== null ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1.5 text-11 text-destructive">
            {error}
          </div>
        ) : (
          <>
            <button
              type="button"
              onClick={() => setOpen((value) => !value)}
              className="flex w-full cursor-pointer items-center gap-1.5 text-left text-11 text-muted-foreground transition-colors hover:text-foreground"
              aria-expanded={open}
            >
              {open ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
              <Paperclip className="size-3 opacity-70" />
              <span className="font-medium text-foreground">
                {totalCount} document{totalCount === 1 ? '' : 's'}
              </span>
              <span className="text-muted-foreground">in this conversation</span>
            </button>
            {open ? (
              <div className="mt-1.5 flex flex-col gap-2 pb-1">
                {hasLivePreviews ? (
                  <ConversationAttachmentGroup
                    group={{
                      key: 'live-previews',
                      label: 'Uploading with this turn…',
                      attachments: livePreviews
                    }}
                    pending
                  />
                ) : null}
                {grouped.map((group) => (
                  <ConversationAttachmentGroup
                    key={group.key}
                    group={group}
                    onOpenAttachment={openAttachment}
                  />
                ))}
              </div>
            ) : null}
          </>
        )}
      </div>
      <SourceDocViewer {...viewerProps} />
    </>
  )
}

interface AttachmentGroup {
  key: string
  label: string
  attachments: ConversationAttachment[]
}

function groupAttachmentsByRun(attachments: ConversationAttachment[]): AttachmentGroup[] {
  const groups = new Map<string, AttachmentGroup>()
  for (const attachment of attachments) {
    const runId = attachment.run_id ?? null
    const key = runId === null ? 'unattached' : `run:${runId}`
    let group = groups.get(key)
    if (group === undefined) {
      group = {
        key,
        label:
          runId === null
            ? 'Uploaded outside a turn'
            : `Uploaded ${formatRelativeTime(attachment.created_at)}`,
        attachments: []
      }
      groups.set(key, group)
    }
    group.attachments.push(attachment)
  }
  const ordered: AttachmentGroup[] = []
  for (const [key, group] of groups.entries()) {
    if (key !== 'unattached') ordered.push(group)
  }
  const stray = groups.get('unattached')
  if (stray !== undefined) ordered.push(stray)
  return ordered
}

function ConversationAttachmentGroup({
  group,
  pending = false,
  onOpenAttachment
}: {
  group: AttachmentGroup
  pending?: boolean
  onOpenAttachment?: (attachment: ConversationAttachment) => void
}) {
  return (
    <div className="flex flex-col gap-1">
      <div
        className={cn(
          'flex items-center gap-1.5 text-10 tracking-[0.04em] uppercase',
          pending ? 'text-primary italic' : 'text-muted-foreground'
        )}
      >
        {pending ? (
          <span className="inline-block size-1.5 animate-pulse rounded-full bg-primary" />
        ) : null}
        {group.label}
      </div>
      <ul className="flex flex-col gap-0.5">
        {group.attachments.map((attachment) => (
          <ConversationAttachmentRow
            key={attachment.id}
            attachment={attachment}
            pending={pending}
            onOpen={onOpenAttachment === undefined ? undefined : () => onOpenAttachment(attachment)}
          />
        ))}
      </ul>
    </div>
  )
}

function ConversationAttachmentRow({
  attachment,
  pending = false,
  onOpen
}: {
  attachment: ConversationAttachment
  pending?: boolean
  onOpen?: () => void
}) {
  const sizeLabel = formatBytes(attachment.size_bytes)
  const title = `${attachment.filename}\n${attachment.mime_type} · ${sizeLabel} · ${new Date(
    attachment.created_at
  ).toLocaleString()}`
  const content = (
    <>
      <FileText className="size-3 shrink-0 opacity-70" />
      <span className="min-w-0 flex-1 truncate font-medium text-foreground" title={title}>
        {attachment.filename}
      </span>
      <span className="shrink-0 text-10 tabular-nums text-muted-foreground">{sizeLabel}</span>
    </>
  )
  return (
    <li
      className={cn(
        'min-w-0 rounded-md border bg-background text-11',
        pending && 'border-dashed border-primary/40 bg-primary/5'
      )}
    >
      {onOpen === undefined ? (
        <div className="flex min-w-0 items-center gap-1.5 px-2 py-1">{content}</div>
      ) : (
        <button
          type="button"
          className="flex w-full min-w-0 cursor-pointer items-center gap-1.5 rounded-md px-2 py-1 text-left transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={`Preview ${attachment.filename}`}
          onClick={onOpen}
        >
          {content}
        </button>
      )}
    </li>
  )
}
